import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import { Build } from '@mui/icons-material';
import BaseTab from './BaseTab';

interface FrontendUIUXTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const FrontendUIUXTab: React.FC<FrontendUIUXTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Frontend UI/UX Design System"
      emoji="🎨"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Comprehensive Design System
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ bgcolor: '#051814', color: '#fff', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: props.goldColor, mb: 2 }}>
                Color Palette
              </Typography>
              {[
                { name: 'Primary', color: '#051814', text: '#fff' },
                { name: 'Secondary', color: '#C4B66F', text: '#000' },
                { name: 'Accent', color: '#0F3B2E', text: '#fff' },
                { name: 'Background Light', color: '#FAFAFA', text: '#000' },
                { name: 'Background Dark', color: '#000000', text: '#fff' },
              ].map((color, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: color.color,
                    color: color.text,
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: color.color,
                      border: '2px solid #fff',
                      mr: 2,
                    }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {color.name}
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 'auto' }}>
                    {color.color}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', bgcolor: alpha(props.goldColor, 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: props.goldColor, mb: 2 }}>
                Typography
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1, color: props.colors.textPrimary }}>
                  Primary Font: Inter
                </Typography>
                <Typography variant="body2" color={props.colors.textSecondary}>
                  Clean, modern sans-serif typeface optimized for readability
                </Typography>
              </Box>
              <Box>
                <Typography variant="h1" sx={{ fontSize: '2.5rem', mb: 1, color: props.colors.textPrimary }}>
                  Headings
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: props.colors.textPrimary }}>
                  Body text with optimal line height
                </Typography>
                <Typography variant="caption" color={props.colors.textSecondary}>
                  Captions and small text
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', bgcolor: alpha('#0F3B2E', 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#0F3B2E', mb: 2 }}>
                Responsive Design
              </Typography>
              <List>
                {[
                  { breakpoint: 'Mobile', size: '< 768px' },
                  { breakpoint: 'Tablet', size: '768px - 1024px' },
                  { breakpoint: 'Desktop', size: '> 1024px' },
                ].map((bp, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <Build sx={{ color: [props.goldColor, '#0F3B2E', '#1D5C4A'][idx] }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={bp.breakpoint}
                      secondary={bp.size}
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                      secondaryTypographyProps={{ color: props.colors.textSecondary }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(props.goldColor, 0.1), borderRadius: 1 }}>
                <Typography variant="caption" color={props.goldColor}>
                  Mobile-first responsive design approach
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
        Component Library
      </Typography>
      <Grid container spacing={2}>
        {[
          'Material-UI Components',
          'Custom Café Components',
          'Community Components',
          'Chat Components',
          'Profile Components',
          'Admin Components',
        ].map((component, idx) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center', 
              borderTop: `3px solid ${[props.goldColor, '#0F3B2E', '#1D5C4A', '#051814', props.goldColor, '#0F3B2E'][idx]}`,
              bgcolor: alpha([props.goldColor, '#0F3B2E', '#1D5C4A', '#051814', props.goldColor, '#0F3B2E'][idx], 0.05)
            }}>
              <Typography variant="body2" fontWeight={500} color={props.colors.textPrimary}>
                {component}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </BaseTab>
  );
};

export default FrontendUIUXTab;